import { useState, useEffect } from "react";
import {
  getAdminVideos,
  createVideo,
  updateVideo,
  deleteVideo,
  type VideoCatalogEntry,
  type VideoCatalogCreate,
} from "../../services/videoService";

export function VideoCatalogPanel() {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [videos, setVideos] = useState<VideoCatalogEntry[]>([]);

  // Form state for new video
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<VideoCatalogCreate>({
    youtube_video_id: "",
    title: "",
    description: "",
    category: "product",
    sort_order: 0,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    loadVideos();
  }, [expanded]);

  const loadVideos = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminVideos();
      setVideos(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.youtube_video_id || !formData.title) {
      setError("YouTube Video ID and Title are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createVideo(formData);
      setSuccess("Video added successfully");
      setShowForm(false);
      setFormData({ youtube_video_id: "", title: "", description: "", category: "product", sort_order: 0 });
      await loadVideos();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create video");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublished = async (video: VideoCatalogEntry) => {
    try {
      await updateVideo(video.id, { is_published: !video.is_published });
      await loadVideos();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update video");
    }
  };

  const handleDelete = async (video: VideoCatalogEntry) => {
    if (!confirm(`Delete "${video.title}"? This cannot be undone.`)) return;
    try {
      await deleteVideo(video.id);
      setSuccess("Video deleted");
      await loadVideos();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete video");
    }
  };

  return (
    <div className="rounded-xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-text-primary">Video Catalog</div>
            <div className="text-xs text-text-secondary">
              Manage YouTube video entries
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-text-secondary transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-surface-700/50">
          {loading && (
            <div className="text-center py-4 text-text-secondary text-sm">Loading...</div>
          )}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
              {success}
            </div>
          )}

          {/* Add Video Button */}
          <div className="pt-3">
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="px-3 py-1.5 text-xs rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
            >
              {showForm ? "Cancel" : "+ Add Video"}
            </button>
          </div>

          {/* Add Video Form */}
          {showForm && (
            <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/50 space-y-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">YouTube Video ID *</label>
                <input
                  type="text"
                  value={formData.youtube_video_id}
                  onChange={(e) => setFormData({ ...formData, youtube_video_id: e.target.value })}
                  placeholder="e.g. dQw4w9WgXcQ"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-surface-800 border border-surface-600 text-text-primary placeholder-text-secondary/50 focus:border-cyan-500/50 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Video title"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-surface-800 border border-surface-600 text-text-primary placeholder-text-secondary/50 focus:border-cyan-500/50 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-surface-800 border border-surface-600 text-text-primary placeholder-text-secondary/50 focus:border-cyan-500/50 focus:outline-none resize-none"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-text-secondary mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as "product" | "tutorial" | "update" })}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-surface-800 border border-surface-600 text-text-primary focus:border-cyan-500/50 focus:outline-none"
                  >
                    <option value="product">Product</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="update">Update</option>
                  </select>
                </div>
                <div className="w-24">
                  <label className="block text-xs text-text-secondary mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-surface-800 border border-surface-600 text-text-primary focus:border-cyan-500/50 focus:outline-none"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleCreate}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Video"}
              </button>
            </div>
          )}

          {/* Video List */}
          {!loading && videos.length === 0 && !showForm && (
            <div className="text-center py-4 text-text-secondary text-sm">
              No videos yet. Click "+ Add Video" to add one.
            </div>
          )}

          {videos.map((video) => (
            <div
              key={video.id}
              className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/50 flex items-center gap-3"
            >
              {/* Thumbnail preview */}
              <img
                src={`https://img.youtube.com/vi/${video.youtube_video_id}/mqdefault.jpg`}
                alt={video.title}
                className="w-20 h-12 rounded object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text-primary truncate">{video.title}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      video.category === "product"
                        ? "bg-cyan-500/20 text-cyan-400"
                        : video.category === "tutorial"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-amber-500/20 text-amber-400"
                    }`}
                  >
                    {video.category}
                  </span>
                  <span className="text-xs text-text-secondary">#{video.sort_order}</span>
                  {!video.is_published && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
                      Draft
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleTogglePublished(video)}
                  className={`px-2 py-1 text-xs rounded ${
                    video.is_published
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-amber-500/20 text-amber-400"
                  } hover:opacity-80 transition-opacity`}
                  title={video.is_published ? "Unpublish" : "Publish"}
                >
                  {video.is_published ? "Published" : "Draft"}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(video)}
                  className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
